import React, { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const ENV = (process.env.REACT_APP_ENVIRONMENT || 'staging').toLowerCase();
const DBG = process.env.NODE_ENV === 'development';

const L = (...args) => { if (DBG) console.log('[SIG_GATE]', ...args); };

function getPriceId(plan, selectedPlanPriceId) {
  if (selectedPlanPriceId) return selectedPlanPriceId;
  if (plan === 'pro') {
    return ENV === 'prod'
      ? process.env.REACT_APP_PRICE_PRO_MONTHLY_LIVE
      : process.env.REACT_APP_PRICE_PRO_MONTHLY_TEST;
  }
  if (plan === 'enterprise') {
    return ENV === 'prod'
      ? process.env.REACT_APP_PRICE_ENTERPRISE_MONTHLY_LIVE
      : process.env.REACT_APP_PRICE_ENTERPRISE_MONTHLY_TEST;
  }
  return null;
}

async function issueOrgJwt(getToken) {
  L('issueOrgJwt: start');
  const clerkJwt = await getToken();
  if (!clerkJwt) { L('issueOrgJwt: no clerk JWT'); return null; }
  L('issueOrgJwt: calling /auth/issue-jwt');
  const r = await fetch(`${API_BASE}/auth/issue-jwt`, {
    headers: { Authorization: `Bearer ${clerkJwt}` },
    credentials: 'omit',
  });
  L('issueOrgJwt: response', r.status);
  if (r.status === 404) return { needOrg: true };
  if (!r.ok) return null;
  const j = await r.json();
  L('issueOrgJwt: payload', j);
  return {
    token: j.token,
    plan: j.plan || j?.subscription?.tier,
    status: j.billing_status || j?.subscription?.status
  };
}

async function billingMe(orgJwt) {
  L('billingMe: calling /billing/me');
  const r = await fetch(`${API_BASE}/billing/me`, {
    headers: { Authorization: `Bearer ${orgJwt}` },
    credentials: 'omit',
  });
  L('billingMe: response', r.status);
  if (!r.ok) return {};
  const txt = await r.text();
  if (!txt) return {};
  let j;
  try { j = JSON.parse(txt); } catch { L('billingMe: bad json'); return {}; }
  L('billingMe: payload', j);
  const s = j.subscription || {};
  return {
    customerId: s.stripeCustomerId || s.stripe_customer_id,
    tier: (s.tier || 'free').toLowerCase(),
    status: (s.status || 'none').toLowerCase(),
  };
}

async function ensureCustomer(orgJwt) {
  L('ensureCustomer: calling /billing/create-customer');
  const r = await fetch(`${API_BASE}/billing/create-customer`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${orgJwt}` },
    credentials: 'omit',
  });
  L('ensureCustomer: response', r.status);
  return r.ok;
}

async function startCheckout(orgJwt, priceId) {
  const origin = window.location.origin;
  const body = {
    PriceId: priceId,
    SuccessUrl: `${origin}/dashboard?billing=success`,
    CancelUrl: `${origin}/pricing?billing=cancel`,
  };
  L('startCheckout: calling /billing/create-checkout-session', body);
  const r = await fetch(`${API_BASE}/billing/create-checkout-session`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${orgJwt}`,
      'Content-Type': 'application/json',
    },
    credentials: 'omit',
    body: JSON.stringify(body),
  });
  L('startCheckout: response', r.status);
  if (!r.ok) return null;
  const j = await r.json();
  L('startCheckout: payload', j);
  return j.url || null;
}

export default function SignInGate({ children }) {
  const { isSignedIn, getToken } = useAuth();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let navigated = false;

    const go = (path, note) => {
      L('navigate →', path, note || '');
      if (!cancelled && !navigated) {
        navigated = true;
        navigate(path);
      }
    };

    const clearOrgNameCaches = () => {
      const keys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith('org_name')) keys.push(k);
      }
      keys.forEach(k => sessionStorage.removeItem(k));
    };

    const run = async () => {
      L('mounted. isSignedIn=', isSignedIn, 'ENV=', ENV, 'API_BASE=', API_BASE);
      if (!isSignedIn) { go('/sign-in', 'not signed in'); return; }

      localStorage.removeItem('org_jwt');
      clearOrgNameCaches();

      try {
        const issued = await issueOrgJwt(getToken);
        if (!issued) { go('/sign-in', 'issue-jwt failed'); return; }
        if (issued.needOrg) { go('/signup-choice?reason=need-org', 'needs org'); return; }

        const orgJwt = issued.token;
        if (!orgJwt) { go('/sign-in', 'no org jwt'); return; }
        localStorage.setItem('org_jwt', orgJwt);

        const selectedPlan = (() => {
          try { return JSON.parse(localStorage.getItem('selectedPlan') || 'null'); } catch { return null; }
        })();
        L('selectedPlan:', selectedPlan);

        const me = await billingMe(orgJwt);

        const paidStatuses = new Set(['active', 'trialing']);
        const paidTiers = new Set(['pro', 'enterprise']);
        const isPaidActive = paidStatuses.has(me.status) && paidTiers.has(me.tier);

        if (isPaidActive || sessionStorage.getItem('skip_checkout') === '1') {
          L('paid + active detected → skipping checkout');
          sessionStorage.setItem('skip_checkout', '1');
          localStorage.removeItem('selectedPlan');
          if (children) { if (!cancelled) setReady(true); } else { go('/dashboard', 'paid-active'); }
          return;
        }

        const intendedTier =
          (selectedPlan?.tier?.toLowerCase()) ||
          (issued.plan?.toLowerCase()) ||
          me.tier ||
          'free';

        const status =
          (issued.status?.toLowerCase()) ||
          me.status ||
          'none';

        L('decision:', { intendedTier, status });

        const needsCheckout = (intendedTier === 'pro' || intendedTier === 'enterprise') && status !== 'active';

        if (needsCheckout && !sessionStorage.getItem('checkout_in_progress')) {
          sessionStorage.setItem('checkout_in_progress', '1');
          L('checkout required');

          if (!me.customerId) {
            const ok = await ensureCustomer(orgJwt);
            if (!ok) { sessionStorage.removeItem('checkout_in_progress'); go('/pricing?reason=missing-customer'); return; }
          }

          const priceId = getPriceId(intendedTier, selectedPlan?.priceId);
          L('priceId resolved:', priceId);
          if (!priceId) { sessionStorage.removeItem('checkout_in_progress'); go('/pricing?reason=missing-price'); return; }

          const url = await startCheckout(orgJwt, priceId);
          if (url) { L('redirecting to Stripe:', url); window.location.href = url; return; }

          sessionStorage.removeItem('checkout_in_progress');
          go('/pricing?reason=checkout-error');
          return;
        }

        sessionStorage.removeItem('checkout_in_progress');
        if (children) {
          if (!cancelled) { L('rendering children'); setReady(true); }
        } else {
          go('/dashboard', 'default');
        }
      } catch (e) {
        L('exception:', e);
        go('/signup-choice?reason=need-org', 'catch');
      }
    };

    run();
    return () => { cancelled = true; };
  }, [isSignedIn, getToken, navigate, children]);

  if (children) {
    if (!ready) {
      return (
        <div style={{ padding: 24, color: '#6b7280' }}>
          Checking your organization…
        </div>
      );
    }
    return <>{children}</>;
  }

  return (
    <div className="onboard-wrapper" style={{ padding: 24, color: '#6b7280' }}>
      Preparing your account…
    </div>
  );
}
